import { useEffect, useState } from 'react';
import type { AppConfig, PlanPeriod, PlanTier } from '@tellvo/setup-protocol';
import { lang, list, t, type Key } from './i18n/index.js';
import type { Notice, SetupStore } from './store.js';

const TIERS: { tier: PlanTier; name: Key; limits: Key }[] = [
  { tier: 'personal', name: 'plan.personal', limits: 'plan.personal.limits' },
  { tier: 'family', name: 'plan.family', limits: 'plan.family.limits' },
  { tier: 'pro', name: 'plan.pro', limits: 'plan.pro.limits' },
];
/** Personal covers three accounts; more need Pro. */
const PERSONAL_ACCOUNTS = 3;

/**
 * The plan block on Free (tellvo-voip-setup #13, variant A): the person's own lines, the three
 * plans with this App Store country's prices from the iPhone, and one button that brings the
 * plans up on the iPhone. The page never sells: the purchase is confirmed there. With the phone
 * away the prices give way to placeholders of the same size and the button to a quiet line, so
 * nothing moves.
 */
export function Plans({ config, store, online, notice }: { config: AppConfig; store: SetupStore; online: boolean; notice: Notice | null }) {
  const plan = config.plan;
  const device = config.app.deviceName ?? 'iPhone';
  const lines = config.accounts.filter((a) => a.account['enabled'] !== false)
    .map((a) => String(a.account['displayName'] ?? '') || String(a.account['domain'] ?? '')).filter(Boolean);
  const fits: PlanTier = lines.length > PERSONAL_ACCOUNTS ? 'pro' : 'personal';
  const [period, setPeriod] = useState<PlanPeriod>('year');
  const [picked, setPicked] = useState<PlanTier>(fits);

  const price = (tier: PlanTier, p: PlanPeriod) => plan?.prices.find((x) => x.tier === tier && x.period === p)?.price;
  const priced = online && (plan?.prices.length ?? 0) > 0;
  const saving = plan?.yearIsTenMonths ? t('plan.save.months') : plan?.yearlySaving ? t('plan.save.percent', { n: plan.yearlySaving }) : null;
  const answer = notice && 'key' in notice && (notice.key === 'notice.plansOpen' || notice.key === 'notice.plansClosed')
    ? { tone: notice.tone, key: notice.key } : null;
  const name = t(TIERS.find((x) => x.tier === picked)?.name ?? 'plan.pro');

  return (
    <section className="card sa" aria-labelledby="h-offer">
      <div className="sa__main">
        <span className={`chip chip--brand chip--plain${plan?.trial ? '' : ' is-off'}`}>{t('plan.trial')}</span>
        <h2 id="h-offer" className="sa__title">{t('plan.title')}</h2>
        <p className="sa__lead">{t('plan.lead', { lines: lines.length ? list(lines) : t('plan.lead.yourLines') })}</p>

        <div className="sa__bill">
          <div className="seg" role="radiogroup" aria-label={t('plan.billing')}>
            {(['month', 'year'] as const).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={period === p} className={period === p ? 'seg--on' : undefined}
                onClick={() => setPeriod(p)}>
                {t(p === 'month' ? 'plan.monthly' : 'plan.yearly')}
                {p === 'year' && saving ? <span className={`save${priced ? '' : ' is-off'}`}>{saving}</span> : null}
              </button>
            ))}
          </div>
          <span className={`field__hint src${priced ? '' : ' is-off'}`}>
            {t('plan.source', { device, region: plan?.region ?? '' })}
          </span>
        </div>

        <div className="picks" role="radiogroup" aria-label={t('plan.pick')}>
          {TIERS.map(({ tier, name: tierName, limits }) => {
            const amount = price(tier, period);
            return (
              <button key={tier} type="button" role="radio" aria-checked={picked === tier}
                className={`pick${picked === tier ? ' pick--on' : ''}`} onClick={() => setPicked(tier)}>
                <span className="pick__top">
                  <b>{t(tierName)}</b>
                  {tier === fits && lines.length ? <span className="chip chip--success chip--plain fit">{t('plan.fits')}</span> : null}
                </span>
                <span className="pick__lim">{t(limits)}</span>
                <span className="pick__price">
                  {priced && amount
                    ? <span><b className="num">{amount}</b> {t(period === 'month' ? 'plan.perMonth' : 'plan.perYear')}</span>
                    : <i className="sk" aria-hidden="true" />}
                </span>
              </button>
            );
          })}
        </div>

        {/* The button and the line for an iPhone away share one cell: the taller sets the height. */}
        <div className="cta">
          {plan ? (
            <>
              <span className={`cta__on${online ? '' : ' is-off'}`}>
                <button type="button" className="button button--primary button--lg" tabIndex={online ? undefined : -1}
                  onClick={() => store.openPlans(picked, period)}>
                  {t(plan.trial ? 'plan.try' : 'plan.subscribe', { plan: name })}
                </button>
                {/* The phone's answer to the button takes the hint's place, next to what was pressed. */}
                {answer ? <span className={`field__hint cta__answer cta__answer--${answer.tone}`} role="status">{t(answer.key)}</span>
                  : <span className="field__hint">{t('plan.ctaHint', { device })}</span>}
              </span>
              <span className={`field__hint cta__away${online ? ' is-off' : ''}`}>{t('plan.away')}</span>
            </>
          ) : <OldApp />}
        </div>
      </div>

      <LockScreen />
    </section>
  );
}

/** An app without the plan in its snapshot cannot be asked to open the plans: the way there by hand. */
function OldApp() {
  return (
    <span className="field__hint cta__away how">
      <span>{t('plan.how')}</span>
      <span className="path">{t('plan.path')}</span>
      <span>{t('plan.currency')}</span>
    </span>
  );
}

/** What the subscription gives, as the iPhone shows it: a call on the Lock Screen with the app closed. */
function LockScreen() {
  const clock = useLockClock();
  return (
    <figure className="sa__phone">
      <div className="ls" role="img" aria-label={t('plan.shot.alt')}>
        <div className="ls__island" />
        <div className="ls__time num">{clock.time}</div>
        <div className="ls__date">{clock.date}</div>
        <div className="ls__call">
          <span className="ls__line">{t('plan.shot.line')}</span>
          <b className="ls__who num">+1 (555) 555-0147</b>
          <span className="ls__line">{t('plan.shot.incoming')}</span>
        </div>
        <div className="ls__btns">
          <span className="ls__b ls__b--no"><Handset /></span>
          <span className="ls__b ls__b--yes"><Handset /></span>
        </div>
      </div>
      <figcaption className="field__hint">{t('plan.shot.caption')}</figcaption>
    </figure>
  );
}

/** lucide "phone", filled: upright to answer, laid down to decline, as on the iPhone. */
/**
 * The visitor's own time and date, as their iPhone's Lock Screen would show them now: in the
 * page language, with the browser's 12- or 24-hour clock and no AM/PM, the weekday capitalised.
 * Moves on at each new minute.
 */
function useLockClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setTimeout(() => setNow(new Date()), 60_000 - (now.getTime() % 60_000));
    return () => clearTimeout(timer);
  }, [now]);
  const locale = (typeof navigator === 'undefined' ? [] : navigator.languages ?? []).find((tag) => tag.toLowerCase().startsWith(lang)) ?? lang;
  const twentyFour = /h2[34]/.test(new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hourCycle ?? '');
  const time = new Intl.DateTimeFormat(locale, { hour: twentyFour ? '2-digit' : 'numeric', minute: '2-digit' })
    .formatToParts(now).filter((part) => part.type === 'hour' || part.type === 'minute' || (part.type === 'literal' && part.value.trim() !== ''))
    .map((part) => part.value).join('');
  const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric' }).format(now);
  return { time, date: date.charAt(0).toLocaleUpperCase(locale) + date.slice(1) };
}

function Handset() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
    </svg>
  );
}
